// src/config/supabase.js
import { createClient } from '@supabase/supabase-js';
import { isNative, authRedirectOrigin } from '../platform';
import { preferencesStorage } from '../platform/preferencesStorage';

// Configuration supporting both old and new key formats
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY; // Can be anon key OR publishable key

// Detect key type
const isPublishableKey = supabaseKey?.startsWith('sb_publishable_');
const isSecretKey = supabaseKey?.startsWith('sb_secret_');

// Validate configuration
export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey);

// Stable auth storage key. Never derive this from the URL: when the auth domain
// changed (vcaugcmjpfyokvlmxeym.supabase.co -> auth.tekstak.app) the derived key
// changed with it, orphaning every session and in-flight PKCE verifier.
const AUTH_STORAGE_KEY = 'sb-tekstak-auth-token';

// Remove entries from the pre-pinned-key eras; stale entries left PKCE sign-in
// broken in returning browsers until users manually cleared site data.
try {
  Object.keys(window.localStorage)
    .filter((k) => /^sb-(vcaugcmjpfyokvlmxeym|auth)-auth-token/.test(k))
    .forEach((k) => window.localStorage.removeItem(k));
} catch (e) {
  // Storage unavailable (private mode, disabled cookies) — nothing to clean.
}

// Development warnings
if (!isSupabaseConfigured && process.env.NODE_ENV === 'development') {
  console.warn(
    '⚠️ Supabase Configuration Missing\n\n' +
    'Add these to your .env file:\n' +
    'REACT_APP_SUPABASE_URL=your_supabase_url\n' +
    'REACT_APP_SUPABASE_ANON_KEY=your_publishable_or_anon_key\n\n' +
    'Supports both legacy (JWT) and new (sb_publishable_) keys.'
  );
}

// Warn about secret keys in frontend
if (isSecretKey && process.env.NODE_ENV === 'development') {
  console.error(
    '🚨 SECURITY WARNING: Never use secret keys (sb_secret_) in frontend applications!\n' +
    'Secret keys should only be used in backend/server environments.\n' +
    'Use publishable keys (sb_publishable_) or anon keys instead.'
  );
}

// Create client with proper configuration
export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        // Native: Preferences-backed storage — iOS can purge WKWebView
        // localStorage under storage pressure, silently logging users out.
        // Web keeps localStorage (sessions there predate this adapter).
        storage: isNative ? preferencesStorage : window.localStorage,
        storageKey: AUTH_STORAGE_KEY
      },
      global: {
        headers: {
          'x-application': 'tekstak',
          // For new publishable keys, the apikey header is automatically set
          ...(isPublishableKey ? {} : { apikey: supabaseKey })
        }
      },
      db: {
        schema: 'public'
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        },
        // New keys have 24-hour connection limit without auth
        ...(isPublishableKey && {
          heartbeatIntervalMs: 30000
        })
      }
    })
  : null;

// Export configuration info
export const config = {
  url: supabaseUrl,
  key: supabaseKey,
  keyType: isPublishableKey ? 'publishable' : 'legacy',
  isConfigured: isSupabaseConfigured
};

// Log configuration status (development only)
if (process.env.NODE_ENV === 'development' && isSupabaseConfigured) {
  console.log('✅ Supabase configured with:', {
    url: supabaseUrl,
    keyType: config.keyType,
    keyPrefix: supabaseKey?.substring(0, 15) + '...'
  });
}

// Helper to check if we're using new API keys
export const isUsingNewKeys = () => isPublishableKey;

// Auth helpers remain the same
export const auth = {
  async signIn(email, password) {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.auth.signInWithPassword({ email, password });
  },
  
  async signUp(email, password, metadata = {}) {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.auth.signUp({ 
      email, 
      password,
      options: { data: metadata }
    });
  },
  
  async signOut() {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.auth.signOut();
  },
  
  async resetPassword(email) {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.auth.resetPasswordForEmail(email, {
      // Emailed link — must return to the website. On native
      // window.location.origin is capacitor://localhost, which Supabase
      // rejects as a redirect target (see platform/authRedirectOrigin).
      redirectTo: `${authRedirectOrigin()}/reset-password`
    });
  },
  
  async getSession() {
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },
  
  onAuthStateChange(callback) {
    if (!supabase) return { data: null, error: new Error('Supabase not configured') };
    return supabase.auth.onAuthStateChange(callback);
  }
};

// Database helpers
export const db = {
  from: (table) => supabase?.from(table),
  rpc: (fn, params) => supabase?.rpc(fn, params),
  channel: (name) => supabase?.channel(name)
};

// Storage helpers
export const storage = {
  from: (bucket) => supabase?.storage.from(bucket),
  
  async uploadFile(bucket, path, file, options = {}) {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.storage.from(bucket).upload(path, file, options);
  },
  
  async downloadFile(bucket, path) {
    if (!supabase) throw new Error('Supabase not configured');
    return supabase.storage.from(bucket).download(path);
  },
  
  getPublicUrl(bucket, path) {
    if (!supabase) return null;
    return supabase.storage.from(bucket).getPublicUrl(path);
  }
};

export default supabase;